<template>
	<div class="business-directory-importer">
		<div class="d-flex align-items-center mb-3">
			<h6 class="mb-0">Import from CSV</h6>
			<button v-if="step !== 'idle'" type="button" class="btn btn-sm btn-link ms-auto" @click="resetAll" :disabled="isProcessing">
				<font-awesome-icon :icon="['fas', 'redo']" class="me-1" />
				Start over
			</button>
		</div>

		<!-- Step: Idle -->
		<div v-if="step === 'idle'" class="border rounded p-3 bg-light">
			<p class="text-muted mb-2">Upload a CSV file to bulk-create business directory entries. Images are downloaded automatically when an image URL column is mapped.</p>
			<label class="btn btn-primary mb-0">
				<font-awesome-icon :icon="['fas', 'file-csv']" class="me-1" />
				Choose CSV file
				<input type="file" accept=".csv,text/csv" class="d-none" @change="onFileSelected" />
			</label>
			<small class="d-block text-muted mt-2">Max 50MB, 5000 rows. Comma, semicolon, tab and pipe delimiters are auto-detected.</small>
		</div>

		<!-- Step: Uploading -->
		<div v-if="step === 'uploading'" class="border rounded p-3 bg-light text-center">
			<font-awesome-icon :icon="['fas', 'spinner']" class="fa-spin me-2" />
			Uploading and parsing CSV...
		</div>

		<!-- Step: Mapping -->
		<div v-if="step === 'mapping'" class="border rounded p-3">
			<div class="d-flex flex-wrap align-items-center mb-3">
				<div>
					<strong>{{ uploadInfo.filename }}</strong>
					<small class="text-muted ms-2">{{ uploadInfo.total_rows }} rows · delimiter: <code>{{ delimiterLabel }}</code></small>
				</div>
			</div>

			<h6 class="mt-2">Map CSV columns</h6>
			<div class="table-responsive mb-3">
				<table class="table table-sm align-middle mb-0">
					<thead>
						<tr>
							<th style="width: 30%">Field</th>
							<th>CSV column</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="field in targetFields" :key="field">
							<td>
								<code>{{ field }}</code>
								<span v-if="field === 'name'" class="text-danger ms-1">*</span>
							</td>
							<td>
								<select class="form-select form-select-sm" v-model="mapping[field]">
									<option :value="null">— ignore —</option>
									<option v-for="col in uploadInfo.columns" :key="col" :value="col">{{ col }}</option>
								</select>
							</td>
						</tr>
					</tbody>
				</table>
			</div>

			<h6>Sample preview (first 5 rows)</h6>
			<div class="table-responsive mb-3" style="max-height: 220px; overflow: auto">
				<table class="table table-sm table-bordered">
					<thead>
						<tr>
							<th v-for="col in uploadInfo.columns" :key="col" class="text-nowrap">{{ col }}</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="(row, idx) in uploadInfo.sample" :key="idx">
							<td v-for="(_, cIdx) in uploadInfo.columns" :key="cIdx" class="text-nowrap" style="max-width: 200px; overflow: hidden; text-overflow: ellipsis">
								{{ row[cIdx] }}
							</td>
						</tr>
					</tbody>
				</table>
			</div>

			<div class="d-flex align-items-center mb-2">
				<h6 class="mb-0">Social media</h6>
				<button type="button" class="btn btn-sm btn-outline-primary ms-auto" @click="addSocial">
					<font-awesome-icon :icon="['fas', 'plus']" class="me-1" />
					Add social
				</button>
			</div>
			<p class="text-muted small mb-2">
				Map CSV columns to social platforms. Each row becomes a <code>{ title, value }</code> entry in the directory's <code>socials</code> JSON. Empty values are skipped.
			</p>
			<div v-if="socialsMapping.length" class="mb-3">
				<div v-for="(pair, idx) in socialsMapping" :key="idx" class="row g-2 align-items-center mb-2">
					<div class="col-md-5">
						<select class="form-select form-select-sm" v-model="pair.column">
							<option :value="null">— select CSV column —</option>
							<option v-for="col in uploadInfo.columns" :key="col" :value="col">{{ col }}</option>
						</select>
					</div>
					<div class="col-md-5">
						<input
							type="text"
							class="form-control form-control-sm"
							v-model="pair.title"
							placeholder="Label (e.g. Facebook, Instagram)"
						/>
					</div>
					<div class="col-md-2 text-end">
						<button type="button" class="btn btn-sm btn-link text-danger" @click="removeSocial(idx)" title="Remove">
							<font-awesome-icon :icon="['fas', 'trash']" />
						</button>
					</div>
				</div>
			</div>
			<div v-else class="text-muted small mb-3 fst-italic">No social mappings. Click <strong>Add social</strong> to map a column.</div>

			<h6>Options</h6>
			<div class="row mb-3">
				<div class="col-md-4">
					<label class="form-label">Skip duplicates by</label>
					<select class="form-select form-select-sm" v-model="options.skip_duplicates_by">
						<option :value="null">None</option>
						<option value="name">Name</option>
						<option value="slug">Slug</option>
					</select>
				</div>
				<div class="col-md-4">
					<label class="form-label">Default status</label>
					<select class="form-select form-select-sm" v-model="options.default_status">
						<option value="draft">draft</option>
						<option value="publish">publish</option>
						<option value="private">private</option>
					</select>
				</div>
				<div class="col-md-4 d-flex align-items-end">
					<div class="form-check">
						<input class="form-check-input" type="checkbox" v-model="options.skip_image_download" id="bdImpSkipImages" />
						<label class="form-check-label" for="bdImpSkipImages">Skip image download</label>
					</div>
				</div>
			</div>

			<div v-if="errorMessage" class="alert alert-danger py-2 mb-3">{{ errorMessage }}</div>

			<div class="d-flex justify-content-between">
				<button type="button" class="btn btn-link" @click="resetAll">Cancel</button>
				<button type="button" class="btn btn-primary" :disabled="!canStart || isProcessing" @click="startImport">
					<font-awesome-icon v-if="isProcessing" :icon="['fas', 'spinner']" class="fa-spin me-1" />
					Start import
				</button>
			</div>
		</div>

		<!-- Step: Progress -->
		<div v-if="step === 'progress' || step === 'done'" class="border rounded p-3">
			<div class="d-flex align-items-center mb-2">
				<strong v-if="step === 'progress'">Importing…</strong>
				<strong v-else-if="progress.cancelled">Import cancelled</strong>
				<strong v-else>Import complete</strong>
				<small class="text-muted ms-auto">{{ progress.processed }} / {{ progress.total }}</small>
			</div>
			<div class="progress mb-3" style="height: 18px">
				<div
					class="progress-bar"
					:class="{ 'bg-success': step === 'done' && !progress.cancelled, 'bg-warning': progress.cancelled }"
					role="progressbar"
					:style="{ width: progressPercent + '%' }"
				>
					{{ progressPercent }}%
				</div>
			</div>

			<div class="row text-center mb-3">
				<div class="col">
					<div class="text-muted small">Created</div>
					<div class="h5 mb-0 text-success">{{ progress.created }}</div>
				</div>
				<div class="col">
					<div class="text-muted small">Skipped</div>
					<div class="h5 mb-0">{{ progress.skipped }}</div>
				</div>
				<div class="col">
					<div class="text-muted small">Failed</div>
					<div class="h5 mb-0 text-danger">{{ progress.failed }}</div>
				</div>
			</div>

			<div v-if="progress.errors && progress.errors.length" class="mb-3">
				<a href="#" @click.prevent="showErrors = !showErrors" class="text-decoration-none">
					<font-awesome-icon :icon="['fas', showErrors ? 'chevron-down' : 'chevron-right']" class="me-1" />
					{{ progress.errors.length }} error(s)
				</a>
				<div v-if="showErrors" class="mt-2 border rounded p-2 bg-light" style="max-height: 220px; overflow: auto">
					<div v-for="(err, idx) in progress.errors" :key="idx" class="small font-monospace">
						<span class="badge bg-danger me-1">row {{ err.row }}</span>{{ err.message }}
					</div>
				</div>
			</div>

			<div class="d-flex justify-content-between">
				<button v-if="step === 'progress'" type="button" class="btn btn-outline-danger btn-sm" @click="cancelImport" :disabled="cancelling">
					<font-awesome-icon v-if="cancelling" :icon="['fas', 'spinner']" class="fa-spin me-1" />
					Cancel
				</button>
				<button v-else type="button" class="btn btn-primary btn-sm" @click="resetAll">Start new import</button>
				<router-link v-if="step === 'done'" :to="{ name: 'business' }" class="btn btn-outline-primary btn-sm">Open business directory</router-link>
			</div>
		</div>
	</div>
</template>

<script>
import juniperApi from '@/api/juniperApi'

const TARGET_FIELDS = [
	'name',
	'slug',
	'content',
	'address',
	'latitude',
	'longitude',
	'email',
	'phone',
	'website',
	'status',
	'category',
	'image_url',
]
const CHUNK_LIMIT = 20

export default {
	name: 'BusinessDirectoryImporter',
	props: {
		clientId: {
			type: [Number, String],
			required: true,
		},
	},
	data() {
		return {
			step: 'idle',
			targetFields: TARGET_FIELDS,
			uploadInfo: {
				filename: '',
				columns: [],
				sample: [],
				total_rows: 0,
				delimiter: ',',
			},
			mapping: this.emptyMapping(),
			socialsMapping: [],
			options: {
				skip_duplicates_by: null,
				default_status: 'draft',
				skip_image_download: false,
			},
			token: null,
			progress: {
				processed: 0,
				total: 0,
				created: 0,
				failed: 0,
				skipped: 0,
				errors: [],
				cancelled: false,
			},
			offset: 0,
			isProcessing: false,
			cancelling: false,
			showErrors: false,
			errorMessage: '',
		}
	},
	computed: {
		canStart() {
			return !!this.mapping.name
		},
		progressPercent() {
			if (!this.progress.total) return 0
			return Math.min(100, Math.floor((this.progress.processed / this.progress.total) * 100))
		},
		delimiterLabel() {
			const d = this.uploadInfo.delimiter
			if (d === '\t') return '\\t (tab)'
			return d
		},
	},
	methods: {
		emptyMapping() {
			const m = {}
			TARGET_FIELDS.forEach(f => {
				m[f] = null
			})
			return m
		},
		resetAll() {
			this.step = 'idle'
			this.uploadInfo = { filename: '', columns: [], sample: [], total_rows: 0, delimiter: ',' }
			this.mapping = this.emptyMapping()
			this.socialsMapping = []
			this.options = { skip_duplicates_by: null, default_status: 'draft', skip_image_download: false }
			this.token = null
			this.progress = { processed: 0, total: 0, created: 0, failed: 0, skipped: 0, errors: [], cancelled: false }
			this.offset = 0
			this.isProcessing = false
			this.cancelling = false
			this.showErrors = false
			this.errorMessage = ''
		},
		addSocial() {
			this.socialsMapping.push({ column: null, title: '' })
		},
		removeSocial(idx) {
			this.socialsMapping.splice(idx, 1)
		},
		onFileSelected(evt) {
			const file = evt.target.files && evt.target.files[0]
			evt.target.value = ''
			if (!file) return
			this.uploadFile(file)
		},
		async uploadFile(file) {
			this.errorMessage = ''
			this.step = 'uploading'
			const formData = new FormData()
			formData.append('client_id', this.clientId)
			formData.append('file', file)
			try {
				const { data } = await juniperApi.post('/business-directories/import/upload', formData, {
					headers: { 'Content-Type': 'multipart/form-data' },
				})
				this.uploadInfo = {
					filename: file.name,
					columns: data.columns,
					sample: data.sample,
					total_rows: data.total_rows,
					delimiter: data.delimiter,
				}
				this.token = data.token
				this.mapping = { ...this.emptyMapping(), ...(data.auto_mapping || {}) }
				this.socialsMapping = Array.isArray(data.auto_socials_mapping)
					? data.auto_socials_mapping.map(p => ({ column: p.column, title: p.title }))
					: []
				this.step = 'mapping'
			} catch (e) {
				this.errorMessage = (e.response && e.response.data && e.response.data.message) || 'Upload failed.'
				this.step = 'idle'
			}
		},
		async startImport() {
			this.errorMessage = ''
			this.isProcessing = true
			try {
				await juniperApi.post('/business-directories/import/start', {
					client_id: this.clientId,
					token: this.token,
					mapping: this.mapping,
					options: {
						...this.options,
						socials_mapping: this.socialsMapping
							.filter(p => p.column && p.title && p.title.trim())
							.map(p => ({ column: p.column, title: p.title.trim() })),
					},
				})
				this.progress = {
					processed: 0,
					total: this.uploadInfo.total_rows,
					created: 0,
					failed: 0,
					skipped: 0,
					errors: [],
					cancelled: false,
				}
				this.offset = 0
				this.step = 'progress'
				this.processNextChunk()
			} catch (e) {
				this.errorMessage = (e.response && e.response.data && e.response.data.message) || 'Failed to start import.'
				this.isProcessing = false
			}
		},
		async processNextChunk() {
			if (this.step !== 'progress') {
				this.isProcessing = false
				return
			}
			try {
				const { data } = await juniperApi.post('/business-directories/import/process-chunk', {
					client_id: this.clientId,
					token: this.token,
					offset: this.offset,
					limit: CHUNK_LIMIT,
				})
				this.progress.processed = data.processed
				this.progress.total = data.total
				this.progress.created = data.created
				this.progress.failed = data.failed
				this.progress.skipped = data.skipped
				this.progress.errors = data.errors
				this.progress.cancelled = !!data.cancelled
				this.offset = data.next_offset

				if (data.done) {
					this.step = 'done'
					this.isProcessing = false
					return
				}
				this.$nextTick(() => this.processNextChunk())
			} catch (e) {
				this.errorMessage = (e.response && e.response.data && e.response.data.message) || 'Chunk processing failed.'
				this.isProcessing = false
			}
		},
		async cancelImport() {
			if (!this.token) return
			this.cancelling = true
			try {
				await juniperApi.post('/business-directories/import/cancel', {
					client_id: this.clientId,
					token: this.token,
				})
				this.progress.cancelled = true
				this.step = 'done'
			} catch (e) {
				// noop
			} finally {
				this.cancelling = false
				this.isProcessing = false
			}
		},
	},
}
</script>

<style scoped>
.business-directory-importer code {
	font-size: 0.85em;
}
</style>
